export const META_PIXEL_ID = '1659703032428376'

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue?: unknown[]
  push?: unknown
  loaded?: boolean
  version?: string
}

declare global {
  interface Window {
    fbq?: Fbq
    _fbq?: Fbq
    __otPixelReady?: boolean
    __otPixelSentEvents?: Record<string, boolean>
  }
}

/** Loads the Meta Pixel base code once and fires a single PageView. */
export function initMetaPixel(pixelId: string = META_PIXEL_ID): void {
  if (typeof window === 'undefined') return
  // Guards against React re-mounts / StrictMode double effects, which would
  // otherwise send duplicate PageView events.
  if (window.__otPixelReady) return
  window.__otPixelReady = true

  if (!window.fbq) {
    const fbq: Fbq = function (...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args)
      } else {
        fbq.queue?.push(args)
      }
    } as Fbq

    fbq.queue = []
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.push = fbq

    window.fbq = fbq
    window._fbq = fbq

    const existing = document.querySelector(
      'script[src="https://connect.facebook.net/en_US/fbevents.js"]',
    )
    if (!existing) {
      const script = document.createElement('script')
      script.async = true
      script.src = 'https://connect.facebook.net/en_US/fbevents.js'
      document.head.appendChild(script)
    }
  }

  window.fbq?.('init', pixelId)
  trackMetaEvent('PageView')
}

export const PRODUCT_EVENT_PARAMS = {
  content_name: 'OT Clinical Toolkit',
  content_ids: ['ot-clinical-toolkit'],
  content_type: 'product',
  value: 27,
  currency: 'USD',
}

type MetaEvent = 'PageView' | 'ViewContent' | 'InitiateCheckout'

/**
 * Fires a standard Meta Pixel event.
 * Page-level events (PageView, ViewContent) are sent only once per page load;
 * action events such as InitiateCheckout may fire on every click.
 */
export function trackMetaEvent(
  event: MetaEvent,
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined' || !window.fbq) return

  const oncePerPage = event === 'PageView' || event === 'ViewContent'
  if (oncePerPage) {
    if (!window.__otPixelSentEvents) window.__otPixelSentEvents = {}
    if (window.__otPixelSentEvents[event]) return
    window.__otPixelSentEvents[event] = true
  }

  const eventID = `${event}-${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`

  if (params) {
    window.fbq('track', event, params, { eventID })
  } else {
    window.fbq('track', event, {}, { eventID })
  }
}
